import { CoreTooltip } from '@featuring-corp/components';
import { IconHelpOutline } from '@featuring-corp/icons';
import { vars } from '@styles/vanilla-extract/theme.css';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import React, { ReactNode } from 'react';

import {
	barChartClass,
	barChartColor,
	barChartContainerClass,
	barChartValue,
	columnNum,
	containerClass,
	dataItemContainerClass,
	innerClass,
	labelClass,
	valueClass,
	valueContainerClass,
} from './style.css';
import ColoredChartText from '../colored-chart-text/ColoredChartText';

export type BoldVerticalBarChartColorType = 'primary' | 'black' | 'gray' | 'white';

export interface BoldVerticalBarChartData {
	value?: string;
	label?: string;
	valuePercentage?: number;
	colorType?: BoldVerticalBarChartColorType;
	isMain?: boolean;
	isBorder?: boolean;
	children?: ReactNode;
	isUnmeasurable?: boolean;
}

export interface BoldVerticalBarChartProps {
	desc?: ReactNode;
	subDesc?: string;
	data?: BoldVerticalBarChartData[];
	isLoading?: boolean;
	isError?: boolean;
	lackOfPermission?: boolean;
}

const BoldVerticalBarChart = ({
	desc,
	subDesc,
	isLoading,
	isError,
	lackOfPermission,
	...props
}: BoldVerticalBarChartProps) => {
	const chartColorMapper = {
		primary: vars.global.colors.primary[60],
		black: vars.global.colors.gray[80],
		gray: vars.global.colors.gray[30],
		white: vars.global.colors.white,
	} as const;

	/**
	 * 차트 데이터의 isUnmeasurable 필드가 true일 경우, 해당 상태에 맞게 데이터를 세팅합니다.
	 */
	const setUnmeasurableChartData = (chartData: BoldVerticalBarChartData) => {
		if (chartData?.isUnmeasurable) {
			return {
				...chartData,
				value: '측정 불가',
				valuePercentage: 25,
				isBorder: true,
				colorType: 'white',
				children: (
					<CoreTooltip
						text={
							'현재 이 지표에 대한 데이터가 표시 조건을 충족하지 않습니다. 유효한 샘플 크기 확보 또는 측정 범위 기준 충족 시 지표가 자동으로 표시됩니다.'
						}
						autoAdjust
					>
						<IconHelpOutline fill={vars.semantic.color.icon.tertiary} />
					</CoreTooltip>
				),
			} as const;
		} else {
			return chartData;
		}
	};

	const data = props.data
		? isLoading
			? props.data.map((item) => ({
					...item,
					valuePercentage: 60,
					colorType: 'gray' as const,
					label: '분석 중',
					value: '???',
			  }))
			: isError
			? props.data.map((item) => ({
					...item,
					valuePercentage: 60,
					colorType: 'gray' as const,
					label: '분석 불가',
					value: '???',
			  }))
			: lackOfPermission
			? props.data.map((item) => ({
					...item,
					valuePercentage: 60,
					label: 'example',
					value: '00',
			  }))
			: props.data.map(setUnmeasurableChartData)
		: undefined;

	return (
		<div className={containerClass}>
			{desc && (
				<ColoredChartText
					subText={subDesc}
					isLoading={isLoading}
					isError={isError}
					lackOfPermission={lackOfPermission}
				>
					{desc}
				</ColoredChartText>
			)}

			<div
				className={innerClass}
				style={assignInlineVars({
					[columnNum]: `${data?.length}`,
				})}
			>
				{data?.map((item, idx) => (
					<div key={idx} className={dataItemContainerClass}>
						<div className={barChartContainerClass}>
							<div
								className={barChartClass({ isBorder: item?.isBorder })}
								style={assignInlineVars({
									[barChartValue]: `${item?.valuePercentage ?? 60}%`,
									[barChartColor]: item?.colorType
										? chartColorMapper?.[item?.colorType]
										: 'gray',
								})}
							>
								{item?.children}
							</div>
						</div>

						<div className={valueContainerClass}>
							<div className={valueClass({ isMain: item?.isMain })}>{item?.value}</div>
							<div className={labelClass}>{item?.label}</div>
						</div>
					</div>
				))}
			</div>
		</div>
	);
};

export default BoldVerticalBarChart;
